import { cn } from "@reactive-resume/utils";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";

import { placeholdersIn } from "./prompt-hints";

export type NoteFieldHandle = {
  focus: () => void;
  selectRange: (start: number, end: number) => void;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  className?: string;
};

const CHIP =
  "inline rounded-md bg-primary/10 px-1.5 py-0.5 text-primary [box-decoration-break:clone]";
const CHIP_SELECTED =
  "inline rounded-md bg-primary/20 px-1.5 py-0.5 text-primary ring-1 ring-inset ring-primary/35 [box-decoration-break:clone]";

const BLANK_RE = /\[[^\]\n]{1,80}\]/g;

const isChip = (node: Node | null): node is HTMLElement =>
  node instanceof HTMLElement && typeof node.dataset.blank === "string";

const chipToken = (el: HTMLElement) => `[${el.dataset.blank ?? ""}]`;

const chipsIn = (root: HTMLElement): HTMLElement[] =>
  Array.prototype.slice.call(root.querySelectorAll("[data-blank]"));

const makeChip = (label: string) => {
  const span = document.createElement("span");
  span.dataset.blank = label;
  span.contentEditable = "false";
  span.setAttribute("role", "mark");
  span.setAttribute("aria-label", `Blank: ${label}`);
  span.className = CHIP;
  span.textContent = label;
  return span;
};

const serialize = (root: HTMLElement): string => {
  let out = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? "").replace(/\u200B/g, "");
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (typeof node.dataset.blank === "string") {
      out += chipToken(node);
      return;
    }
    if (node.tagName === "BR") {
      out += "\n";
      return;
    }
    if ((node.tagName === "DIV" || node.tagName === "P") && out.length > 0 && !out.endsWith("\n")) {
      out += "\n";
    }
    node.childNodes.forEach((child) => walk(child));
  };
  walk(root);
  return out === "\n" ? "" : out;
};

const renderValue = (root: HTMLElement, value: string) => {
  root.replaceChildren();
  if (!value) return;

  BLANK_RE.lastIndex = 0;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = BLANK_RE.exec(value))) {
    if (match.index > last) {
      root.append(document.createTextNode(value.slice(last, match.index)));
    }
    root.append(makeChip(match[0].slice(1, -1)));
    last = match.index + match[0].length;
  }
  if (last < value.length) {
    root.append(document.createTextNode(value.slice(last)));
  } else if (isChip(root.lastChild)) {
    root.append(document.createTextNode("\u200B"));
  }
};

const nodeLength = (node: Node): number => {
  if (node.nodeType === Node.TEXT_NODE)
    return (node.textContent ?? "").replace(/\u200B/g, "").length;
  if (isChip(node)) return chipToken(node).length;
  if (node instanceof HTMLElement && node.tagName === "BR") return 1;
  let total = 0;
  node.childNodes.forEach((child) => {
    total += nodeLength(child);
  });
  return total;
};

const placeCaret = (node: Node, offset: number) => {
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
};

const selectChip = (root: HTMLElement, chip: HTMLElement) => {
  root.focus();
  const range = document.createRange();
  range.selectNode(chip);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
  paint(root);
};

const selectAll = (root: HTMLElement) => {
  const range = document.createRange();
  range.selectNodeContents(root);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
  paint(root);
};

const rangeCoversRoot = (root: HTMLElement, range: Range): boolean => {
  const full = document.createRange();
  full.selectNodeContents(root);
  return (
    range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
    range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
  );
};

const exactChip = (root: HTMLElement): HTMLElement | null => {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (rangeCoversRoot(root, range) && chipsIn(root).length !== 1) return null;
  const hits = chipsIn(root).filter((el) => range.intersectsNode(el));
  if (hits.length !== 1) return null;
  const chip = hits[0];
  const around = document.createRange();
  around.selectNode(chip);
  const same =
    range.compareBoundaryPoints(Range.START_TO_START, around) === 0 &&
    range.compareBoundaryPoints(Range.END_TO_END, around) === 0;
  return same ? chip : null;
};

const paint = (root: HTMLElement) => {
  const selected = exactChip(root);
  chipsIn(root).forEach((el) => {
    el.className = el === selected ? CHIP_SELECTED : CHIP;
  });
};

const caretAfter = (root: HTMLElement, node: Node) => {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
  paint(root);
};

const caretBefore = (root: HTMLElement, node: Node) => {
  const range = document.createRange();
  range.setStartBefore(node);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
  paint(root);
};

const adjacentChip = (root: HTMLElement, side: "before" | "after"): HTMLElement | null => {
  const sel = window.getSelection();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return null;
  const { startContainer, startOffset } = sel.getRangeAt(0);

  const sibling = (node: Node | null) =>
    side === "before" ? node?.previousSibling ?? null : node?.nextSibling ?? null;

  if (startContainer === root) {
    const node =
      side === "before" ? root.childNodes[startOffset - 1] : root.childNodes[startOffset];
    return isChip(node) ? node : null;
  }
  if (startContainer.nodeType === Node.TEXT_NODE) {
    const text = startContainer as Text;
    const atEdge =
      side === "before" ? startOffset === 0 : startOffset === (text.textContent ?? "").length;
    if (!atEdge) return null;
    const node = sibling(text);
    return isChip(node) ? node : null;
  }
  return null;
};

const removeChip = (root: HTMLElement, chip: HTMLElement, emit: () => void) => {
  const next = chip.nextSibling;
  const prev = chip.previousSibling;
  chip.remove();
  if (next) caretBefore(root, next);
  else if (prev) caretAfter(root, prev);
  else placeCaret(root, 0);
  paint(root);
  emit();
};

const replaceChip = (root: HTMLElement, chip: HTMLElement, text: string, emit: () => void) => {
  if (!text) {
    removeChip(root, chip, emit);
    return;
  }
  const node = document.createTextNode(text);
  chip.replaceWith(node);
  placeCaret(node, text.length);
  paint(root);
  emit();
};

const offsetFromPoint = (root: HTMLElement, container: Node, offset: number): number => {
  let acc = 0;
  let done = false;
  const visit = (node: Node) => {
    if (done) return;
    if (node === container && node.nodeType === Node.TEXT_NODE) {
      acc += (node.textContent ?? "").slice(0, offset).replace(/\u200B/g, "").length;
      done = true;
      return;
    }
    if (isChip(node)) {
      if (node === container) {
        done = true;
        return;
      }
      acc += chipToken(node).length;
      return;
    }
    if (node === container && node instanceof HTMLElement) {
      for (let i = 0; i < offset && i < node.childNodes.length; i += 1) {
        acc += nodeLength(node.childNodes[i]!);
      }
      done = true;
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      acc += (node.textContent ?? "").replace(/\u200B/g, "").length;
      return;
    }
    node.childNodes.forEach((child) => visit(child));
  };
  visit(root);
  return acc;
};

const setSerializedSelection = (root: HTMLElement, start: number, end: number) => {
  const covering = placeholdersIn(serialize(root)).find(
    (item) => item.start === start && item.end === end,
  );
  if (covering) {
    let acc = 0;
    for (const child of Array.from(root.childNodes)) {
      const len = nodeLength(child);
      if (isChip(child) && acc === start) {
        selectChip(root, child);
        return;
      }
      acc += len;
    }
  }

  const point = (target: number): { node: Node; offset: number } => {
    let acc = 0;
    for (const child of Array.from(root.childNodes)) {
      const len = nodeLength(child);
      if (target < acc + len || (target === acc + len && target === acc && len === 0)) {
        if (child.nodeType === Node.TEXT_NODE) {
          const raw = child.textContent ?? "";
          return { node: child, offset: Math.min(target - acc, raw.length) };
        }
        if (isChip(child)) {
          return target <= acc
            ? { node: root, offset: Array.from(root.childNodes).indexOf(child) }
            : { node: root, offset: Array.from(root.childNodes).indexOf(child) + 1 };
        }
      }
      if (target <= acc + len && child.nodeType === Node.TEXT_NODE) {
        return { node: child, offset: Math.min(target - acc, (child.textContent ?? "").length) };
      }
      acc += len;
    }
    return { node: root, offset: root.childNodes.length };
  };

  const a = point(start);
  const b = start === end ? a : point(end);
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
  paint(root);
};

export const NoteField = forwardRef<NoteFieldHandle, Props>(
  ({ value, onChange, onSubmit, disabled, placeholder, autoFocus, maxLength, className }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const lastValue = useRef(value);

    const emit = useCallback(() => {
      const root = editorRef.current;
      if (!root) return;
      let next = serialize(root);
      if (maxLength && next.length > maxLength) next = next.slice(0, maxLength);
      lastValue.current = next;
      onChange(next);
    }, [maxLength, onChange]);

    const normalize = useCallback(() => {
      const root = editorRef.current;
      if (!root) return;
      const next = serialize(root);
      if (placeholdersIn(next).length === chipsIn(root).length && serialize(root) === next) {
        paint(root);
        return;
      }
      const sel = window.getSelection();
      const caret =
        sel && sel.rangeCount > 0
          ? offsetFromPoint(root, sel.getRangeAt(0).startContainer, sel.getRangeAt(0).startOffset)
          : next.length;
      renderValue(root, next);
      lastValue.current = next;
      onChange(next);
      setSerializedSelection(root, caret, caret);
    }, [onChange]);

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      selectRange: (start, end) => {
        const root = editorRef.current;
        if (!root) return;
        root.focus();
        setSerializedSelection(root, start, end);
      },
    }));

    useLayoutEffect(() => {
      const root = editorRef.current;
      if (!root) return;
      if (value === lastValue.current && serialize(root) === value) {
        paint(root);
        return;
      }
      lastValue.current = value;
      renderValue(root, value);
      paint(root);
    }, [value]);

    useEffect(() => {
      if (autoFocus) editorRef.current?.focus();
    }, [autoFocus]);

    useEffect(() => {
      const onSel = () => {
        const root = editorRef.current;
        if (root) paint(root);
      };
      document.addEventListener("selectionchange", onSel);
      return () => document.removeEventListener("selectionchange", onSel);
    }, []);

    return (
      <div className={cn("relative min-h-[10rem] w-full flex-1", className)}>
        {!value ? (
          <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden px-3.5 py-3 text-[15px] font-medium leading-relaxed text-foreground/40">
            {placeholder}
          </div>
        ) : null}
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-placeholder={placeholder}
          aria-disabled={disabled}
          contentEditable={disabled ? false : "true"}
          suppressContentEditableWarning
          spellCheck
          className={cn(
            "absolute inset-0 z-[1] overflow-y-auto whitespace-pre-wrap break-words rounded-2xl border border-border bg-secondary/20 px-3.5 py-3 text-[15px] font-medium leading-relaxed text-foreground outline-none transition-colors",
            "hover:bg-secondary/30 focus:border-primary focus:bg-secondary/30 focus:ring-1 focus:ring-primary/25",
            disabled && "pointer-events-none opacity-60",
          )}
          onMouseDown={(event) => {
            const chip = (event.target as HTMLElement | null)?.closest?.("[data-blank]");
            const root = editorRef.current;
            if (!chip || !root || !root.contains(chip) || !isChip(chip)) return;
            event.preventDefault();
            selectChip(root, chip);
          }}
          onKeyDown={(event) => {
            const root = editorRef.current;
            if (!root || disabled) return;
            const sel = window.getSelection();
            const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;

            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a") {
              event.preventDefault();
              selectAll(root);
              return;
            }

            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              onSubmit?.();
              return;
            }

            if (event.key === "Enter") {
              event.preventDefault();
              if (!sel || !range) return;
              range.deleteContents();
              const newline = document.createTextNode("\n");
              range.insertNode(newline);
              caretAfter(root, newline);
              emit();
              return;
            }

            if (event.key === "Tab") {
              const list = chipsIn(root);
              if (list.length === 0) return;
              event.preventDefault();
              const selected = exactChip(root);
              const after = adjacentChip(root, "after");
              if (!selected && after && !event.shiftKey) {
                selectChip(root, after);
                return;
              }
              const index = selected ? list.indexOf(selected) : -1;
              const next = event.shiftKey
                ? list[index > 0 ? index - 1 : list.length - 1]
                : list[index < list.length - 1 ? index + 1 : 0];
              if (next) selectChip(root, next);
              return;
            }

            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
              const selected = exactChip(root);
              if (selected) {
                event.preventDefault();
                if (event.key === "ArrowRight") caretAfter(root, selected);
                else caretBefore(root, selected);
                return;
              }
              if (sel?.isCollapsed) {
                const next = adjacentChip(root, event.key === "ArrowRight" ? "after" : "before");
                if (next) {
                  event.preventDefault();
                  selectChip(root, next);
                }
              }
              return;
            }

            if (event.key === "Backspace" || event.key === "Delete") {
              if (range && rangeCoversRoot(root, range)) {
                event.preventDefault();
                root.replaceChildren();
                paint(root);
                emit();
                return;
              }
              const selected = exactChip(root);
              if (selected) {
                event.preventDefault();
                removeChip(root, selected, emit);
                return;
              }
              if (sel?.isCollapsed) {
                const neighbor = adjacentChip(root, event.key === "Backspace" ? "before" : "after");
                if (neighbor) {
                  event.preventDefault();
                  removeChip(root, neighbor, emit);
                }
              }
              return;
            }

            if (
              event.key.length === 1 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.altKey &&
              !event.nativeEvent.isComposing
            ) {
              const selected = exactChip(root);
              if (selected) {
                event.preventDefault();
                replaceChip(root, selected, event.key, emit);
              }
            }
          }}
          onPaste={(event) => {
            const root = editorRef.current;
            if (!root || disabled) return;
            event.preventDefault();
            const pasted = event.clipboardData.getData("text/plain");
            const selected = exactChip(root);
            if (selected) {
              replaceChip(root, selected, pasted, emit);
              return;
            }
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            range.deleteContents();
            const node = document.createTextNode(pasted);
            range.insertNode(node);
            caretAfter(root, node);
            emit();
            normalize();
          }}
          onBlur={() => {
            const root = editorRef.current;
            if (!root) return;
            paint(root);
            normalize();
          }}
          onInput={emit}
          onCompositionStart={() => {
            const root = editorRef.current;
            if (!root) return;
            const selected = exactChip(root);
            if (selected) replaceChip(root, selected, "", emit);
          }}
        />
      </div>
    );
  },
);

NoteField.displayName = "NoteField";
